/**
 * Lighthouse Farm — header (01)
 *
 * Owns the mobile drawer (including its search row's hand-off into the
 * predictive-search dialog), the hide-on-scroll bar, and the measured header
 * heights that base.css and any first-paint consumer read.
 *
 * Scroll container: Horizon's squeeze layout makes `.page-wrapper` the page
 * scroller at >=990px (base.css puts html + body at height:100dvh /
 * overflow:hidden there) and `document.scrollingElement` below it. An element
 * scroll event does not bubble, so a document-level listener is deaf on
 * desktop. Everything below goes through @theme/scroll-container, the same
 * helper stock header.js uses, and rebinds when the viewport crosses 990px.
 *
 * Node references are re-resolved on every use. Horizon clones the header
 * during cart hydration (morphSection), so a cached `[data-drawer]` can end up
 * pointing at a detached copy; `:scope > [data-drawer]` always reads the live
 * one. Clicks are delegated for the same reason.
 */
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

(() => {
  const DESKTOP = () => window.matchMedia('(min-width: 750px)').matches;

  /**
   * Upward travel that counts as a reveal — small enough that a trackpad nudge
   * brings the bar back, large enough that rubber-band jitter at either end of
   * the page does not flicker it.
   */
  const REVEAL_EPSILON = 2;

  /**
   * Torn down before a re-init. The theme editor re-renders the section on
   * every reselection, and the previous listener would keep running against a
   * header that is no longer in the document.
   * @type {(() => void) | null}
   */
  let detachScroll = null;

  /**
   * The drawer's scroll lock is global — it lives on documentElement, not on
   * the header — so it outlives the node that set it.
   */
  const lockScroll = (on) => {
    document.documentElement.style.overflow = on ? 'hidden' : '';
  };

  const init = (header) => {
    if (header.dataset.lfHeaderReady) return;
    header.dataset.lfHeaderReady = 'true';

    detachScroll?.();
    detachScroll = null;

    const drawer = () => header.querySelector(':scope > [data-drawer]');
    const menuToggle = () => header.querySelector('.lf-header__menu-toggle');
    const closeButton = () => header.querySelector('[data-drawer-close]');

    let lastFocused = null;

    // A re-render (the theme editor re-renders the section on every
    // reselection) removes an open drawer without ever running closeDrawer, so
    // the lock it set would survive its own drawer and leave the page
    // unscrollable. Every init starts from the closed state: the re-rendered
    // markup already carries `hidden` on the drawer, so only the global lock
    // and the open-state attributes need clearing.
    lockScroll(false);
    header.removeAttribute('data-drawer-open');
    drawer()?.removeAttribute('data-open');
    menuToggle()?.setAttribute('aria-expanded', 'false');

    const isOpen = () => {
      const el = drawer();
      return !!el && el.hasAttribute('data-open');
    };

    const openDrawer = () => {
      const el = drawer();
      if (!el || el.hasAttribute('data-open')) return;

      lastFocused = document.activeElement;
      // The bar has to be down before the drawer slides out of it, and it must
      // still be down when the drawer closes again.
      header.dataset.scrollDirection = 'none';
      header.setAttribute('data-drawer-open', '');
      el.hidden = false;
      // Force layout so the slide-in runs from the closed transform.
      void el.offsetWidth;
      el.setAttribute('data-open', '');
      menuToggle()?.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      closeButton()?.focus();
    };

    const closeDrawer = () => {
      const el = drawer();
      if (!el || !el.hasAttribute('data-open')) return;

      el.removeAttribute('data-open');
      menuToggle()?.setAttribute('aria-expanded', 'false');
      lockScroll(false);

      if (lastFocused instanceof HTMLElement) {
        lastFocused.focus();
        lastFocused = null;
      }

      window.setTimeout(() => {
        const current = drawer();
        if (current && !current.hasAttribute('data-open')) {
          current.hidden = true;
          header.removeAttribute('data-drawer-open');
        }
      }, 350);
    };

    /**
     * The drawer's search row is a trigger, not a form. Touching the field, or
     * pressing Enter in it, hands off to the same predictive-search dialog the
     * bar opens, so there is one search surface on the site and nothing ever
     * lands on /search, which has no design.
     *
     * Order matters. closeDrawer() releases the scroll lock and returns focus
     * to the menu toggle synchronously, which is also what makes the dialog
     * hand focus back there when it closes — the browser restores focus to
     * whatever held it before showModal(). DialogComponent.showDialog() defers
     * showModal() to the next frame, and a closed <dialog> is display:none, so
     * nothing inside it can take focus until after that.
     */
    const openSearchFromDrawer = () => {
      const modal = header.querySelector('#search-modal');
      if (!modal || typeof modal.showDialog !== 'function') return;

      const field = header.querySelector('[data-drawer-search-input]');
      const term = field instanceof HTMLInputElement ? field.value.trim() : '';

      closeDrawer();
      modal.showDialog();

      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const input = modal.querySelector('input.search-input');
          if (!(input instanceof HTMLInputElement)) return;

          input.focus();
          if (!term) return;

          input.value = term;
          // predictive-search.js drops any input event without an inputType,
          // so a bare new Event('input') would move the value but fetch nothing.
          input.dispatchEvent(
            new InputEvent('input', { bubbles: true, inputType: 'insertText', data: term })
          );
        })
      );

      if (field instanceof HTMLInputElement) field.value = '';
    };

    header.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('.lf-header__menu-toggle')) {
        if (isOpen()) closeDrawer();
        else openDrawer();
        return;
      }

      if (target.closest('[data-drawer-search-open]')) {
        openSearchFromDrawer();
        return;
      }

      if (target.closest('[data-drawer-close]')) closeDrawer();
    });

    // The row is not a form, so an Enter keypress in the field has nothing to
    // submit — it is the submit, and it opens the dialog. This is the only
    // submit affordance since the "Enter" pill was dropped (2026-09-06).
    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;

      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-drawer-search-input]')) return;

      event.preventDefault();
      openSearchFromDrawer();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) closeDrawer();
    });

    // base.css positions the first section against --header-height, and
    // --header-group-height is read by Horizon's own drawer/overlay offsets.
    // Stock header.js normally writes both; it is not on the page any more.
    const bar = header.querySelector('.lf-header__bar');
    const group = document.getElementById('header-group');
    const section = header.closest('.shopify-section');

    const setHeights = () => {
      const height = Math.round((bar || header).offsetHeight);
      document.body.style.setProperty('--header-height', `${height}px`);

      // Flow height of the group, matching the walk in theme.liquid and
      // utilities.js: a transparent header is out of flow and contributes 0.
      let groupHeight = header.hasAttribute('data-transparent') ? 0 : height;
      if (group) {
        for (const child of group.children) {
          if (child instanceof HTMLElement && child !== section) groupHeight += child.offsetHeight;
        }
      }
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    };

    setHeights();
    if ('ResizeObserver' in window && bar) new ResizeObserver(setHeights).observe(bar);

    /* ----------------------------------------------------------------------
       Hide on scroll down, reveal on scroll up

       Two attributes drive the CSS: `data-scroll-direction` slides the bar,
       `data-sticky-state` swaps a transparent header to the solid cream
       treatment for the whole pinned run. The bar is never hidden at the top of
       the page, and never while an overlay it owns is open — the drawer is
       position:fixed inside the header, so a transform on the header would trap
       it, and pulling the search trigger offscreen mid-interaction is wrong
       even though the dialog itself sits in the top layer.
       ---------------------------------------------------------------------- */

    const overlayOpen = () =>
      isOpen() || !!header.querySelector('dialog[open]') || !!header.querySelector(':focus-visible');

    let lastScroll = getScrollTop();
    let frame = null;
    let scrollTarget = null;

    const update = () => {
      frame = null;

      const scrollTop = getScrollTop();
      // Hide only once the bar has travelled its own height, so a short scroll
      // never snatches it away.
      const past = scrollTop > (bar ? bar.offsetHeight : 0);

      if (!past || overlayOpen()) {
        header.dataset.scrollDirection = 'none';
        header.dataset.stickyState = past ? 'active' : 'inactive';
        lastScroll = scrollTop;
        return;
      }

      // A restored scroll position (back navigation) lands as one enormous
      // delta rather than a gesture. Arriving somewhere is not scrolling down,
      // so the bar stays put instead of vanishing on a move nobody made.
      if (Math.abs(scrollTop - lastScroll) > window.innerHeight * 1.5) {
        header.dataset.scrollDirection = 'none';
        header.dataset.stickyState = 'active';
        lastScroll = scrollTop;
        return;
      }

      if (scrollTop < lastScroll - REVEAL_EPSILON) header.dataset.scrollDirection = 'up';
      else if (scrollTop > lastScroll) header.dataset.scrollDirection = 'down';

      // Solid for everything except the slide away: a transparent header that
      // faded to cream on its way off the hero would flash against the photo.
      header.dataset.stickyState = header.dataset.scrollDirection === 'down' ? 'inactive' : 'active';
      lastScroll = scrollTop;
    };

    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(update);
    };

    const bind = () => {
      scrollTarget?.removeEventListener('scroll', onScroll);
      scrollTarget = getScrollEventTarget();
      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    };

    // Crossing 990px swaps the page scroller, so the held target goes stale.
    const rebind = () => {
      bind();
      lastScroll = getScrollTop();
      update();
    };

    bind();
    scrollContainerMediaQuery.addEventListener('change', rebind);
    update();

    detachScroll = () => {
      scrollTarget?.removeEventListener('scroll', onScroll);
      scrollTarget = null;
      scrollContainerMediaQuery.removeEventListener('change', rebind);
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };

    window.addEventListener('resize', () => {
      if (DESKTOP() && isOpen()) closeDrawer();
      setHeights();
      update();
    });
  };

  const boot = () => document.querySelectorAll('[data-lf-header]').forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (event) => {
    const header = event.target instanceof Element ? event.target.querySelector('[data-lf-header]') : null;
    if (header) init(header);
  });
})();
